import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton, Grid, Box, IconButton, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { fetchBessStatusBySystemId } from '../redux/slices/bessSlice';
import SystemCard from './SystemCard';

export default function DashboardAppPage({ msSessionInfo }) {
  // ============= Initialization =============
  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const systemLoading = useSelector((state) => state.systems.loading);
  const systemData = useSelector((state) => state.systems);
  const getLastUpdateTimeString = () => `Last Updated ${dayjs().format('hh:mm:ss')}`;
  const dispatch = useDispatch();


  const columns = 3;

  // Calculate how many skeletons to fill the screen
  const cardHeight = 160 + 24;
  const headerHeight = 120; 
  const availableHeight =
    typeof window !== 'undefined' ? window.innerHeight - headerHeight : 800;
  const rows = Math.ceil(availableHeight / cardHeight);
  const skeletonCount = columns * rows;

  // ============= Refresh Data Function =============
  // This function will be called when the refresh button is clicked
  const refreshData = () => {
    // If msSessionInfo and systemData are available, fetch the BESS status for all systems
    if (msSessionInfo && systemData?.data?.length > 0) {
      const systemIds = systemData.data.map((system) => system.id);
      // Dispatch the action to the redux store to fetch BESS status
      dispatch(fetchBessStatusBySystemId({ msSessionInfo, systemIds }));
      // Update the last updated timestamp
      setRefreshCounter((prev) => prev + 1);
    }
    setLastUpdatedTimestamp(getLastUpdateTimeString());
  };

  // ============= Effect to set initial last updated timestamp =============
  useEffect(() => {
    if (msSessionInfo && systemData?.data?.length > 0) {
      setLastUpdatedTimestamp(getLastUpdateTimeString());
    }
  }, [msSessionInfo, systemData.data]);

  return (
    <>
      <Helmet>
        <title>SYSO Home</title>
      </Helmet>
      {/* Display a loading spinner while the system data is being fetched */}
      <Box sx={{ pl: 5, pr: 5 }}>
        <Grid container spacing={3} direction="row" alignItems="flex-end">
          <Grid item xs={6}>
            <Typography variant="h4" sx={{ mb: 5 }}>
              System Summary
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ pt: 2 }}>
            <Grid container alignItems="center" justifyContent="flex-end">
              <Typography variant="caption">{lastUpdatedTimestamp}</Typography>
              <IconButton size="small" aria-label="refresh" onClick={refreshData}>
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>

          {/* Show either system cards or fallback skeletons */}
          {systemLoading && !systemData?.data?.length
            ? Array.from({ length: skeletonCount }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Box sx={{ height: 160, borderRadius: 2, overflow: 'hidden' }}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={160}
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </Grid>
              ))
            : systemData?.data?.map((system) => (
                <SystemCard
                  key={system.id}
                  system={system}
                  msSessionInfo={msSessionInfo}
                  refreshCounter={refreshCounter}
                />
              ))}
        </Grid>
      </Box>
    </>
  );
}